import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
} from '@mui/material';
import { CloudUpload, CheckCircle, Error, Add, Edit, Security, Warning, History } from '@mui/icons-material';
import { ImportResult } from '../types';

interface ImportDialogProps {
  open: boolean;
  schoolName?: string;
  onClose: () => void;
  onValidate: (file: File) => Promise<ImportResult>;
  onExecute: (file: File) => Promise<ImportResult>;
}

// The columns Export writes, in its order; the import reads them by position.
export const IMPORT_COLUMNS = [
  'School Code',
  'First Name',
  'Last Name',
  'Your 10-digit mobile no.',
  'Security PIN',
  'Grade',
  'Section',
  'Roll Number',
  'Maths',
  'Science',
  'English',
];

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  schoolName,
  onClose,
  onValidate,
  onExecute,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ImportResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'upload' | 'validate' | 'import' | 'complete'>('upload');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setValidationResult(null);
      setImportResult(null);
      setStep('upload');
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const result = await onValidate(selectedFile);
      setValidationResult(result);
      setStep('validate');
    } catch (error: unknown) {
      console.error('Validation failed:', error);
      const errorMessage = (error as Error)?.message || 'Validation failed';
      setValidationResult({
        success: false,
        message: errorMessage,
        schoolName: '',
        totalRows: 0,
        validRows: 0,
        invalidRows: 0,
        newStudents: 0,
        returningStudents: 0,
        updatedStudents: 0,
        changes: [],
        errors: [],
        isDryRun: true,
      });
      setStep('validate');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !validationResult?.success) return;

    setLoading(true);
    try {
      const result = await onExecute(selectedFile);
      setImportResult(result);
      setStep('complete');
    } catch (error: unknown) {
      console.error('Import failed:', error);
      const errorMessage = (error as Error)?.message || 'Import failed';
      setImportResult({
        success: false,
        message: errorMessage,
        schoolName: validationResult.schoolName,
        totalRows: validationResult.totalRows,
        validRows: 0,
        invalidRows: validationResult.totalRows,
        newStudents: 0,
        returningStudents: 0,
        updatedStudents: 0,
        changes: [],
        errors: [],
        isDryRun: false,
      });
      setStep('complete');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    setStep('upload');
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Student Data
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload an XLSX file with this school's students{schoolName ? <> for <strong>{schoolName}</strong></> : null}.
        The file must be named for this school - Export names it for you.
      </Typography>
      
      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          mb: 2,
        }}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <input
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          onChange={handleFileSelect}
        />
        <label htmlFor="file-upload">
          <Button variant="contained" component="span">
            Choose File
          </Button>
        </label>
        {selectedFile && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Selected: {selectedFile.name}
          </Typography>
        )}
      </Box>

      <Alert severity="info">
        <Typography variant="body2">
          <strong>File Requirements:</strong>
          <br />• XLSX format only, named for this school
          <br />• Columns, in this order: {IMPORT_COLUMNS.join(', ')}
          <br />• Brothers and sisters may share a mobile number; a family signs in with one Security PIN
          <br />• Security PIN may be blank: a family already on LiveOlympiad keeps theirs, a new family gets one. A PIN given (6 digits, starting 1-9) becomes the family's new PIN.
        </Typography>
      </Alert>
    </Box>
  );

  const renderValidationStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Validation Results
      </Typography>
      
      {validationResult && (
        <>
          <Alert 
            severity={validationResult.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
            icon={validationResult.success ? <CheckCircle /> : <Error />}
          >
            {validationResult.message}
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">School: {validationResult.schoolName}</Typography>
            <Typography variant="body2">Total Rows: {validationResult.totalRows}</Typography>
            <Typography variant="body2" color="success.main">Valid Rows: {validationResult.validRows}</Typography>
            {validationResult.invalidRows > 0 && (
              <Typography variant="body2" color="error.main">Invalid Rows: {validationResult.invalidRows}</Typography>
            )}
            
            {/* Enhanced Statistics */}
            {validationResult.success && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'success.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Add fontSize="small" />
                  <Typography variant="body2" color="success.dark">
                    {validationResult.newStudents || 0} New Students
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'secondary.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <History fontSize="small" />
                  <Typography variant="body2" color="secondary.dark">
                    {validationResult.returningStudents || 0} Returning Students
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'info.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Edit fontSize="small" />
                  <Typography variant="body2" color="info.dark">
                    {validationResult.updatedStudents || 0} Updated Students
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Security PIN Change Warnings */}
          {validationResult.changes && validationResult.changes.some(change => change.changeType === 'SECURITY_PIN_CHANGE') && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<Security />}>
              <Typography variant="body2">
                <strong>Security PIN Changes Detected:</strong> Some families will have their Security PIN changed,
                for every child who shares their mobile number. The old PIN will stop working.
              </Typography>
            </Alert>
          )}

          {/* Detailed Changes Table */}
          {validationResult.changes && validationResult.changes.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Changes to be Applied:
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Change Type</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Old Value</TableCell>
                      <TableCell>New Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResult.changes.map((change, index) => (
                      <TableRow key={index}>
                        <TableCell>{change.rowNumber}</TableCell>
                        <TableCell>{change.studentName}</TableCell>
                        <TableCell>
                          <Chip
                            label={change.changeType.replace(/_/g, ' ')}
                            size="small"
                            color={
                              change.changeType === 'NEW' ? 'success' :
                              change.changeType === 'RETURNING' ? 'secondary' :
                              change.changeType === 'SECURITY_PIN_CHANGE' ? 'warning' : 'info'
                            }
                            icon={
                              change.changeType === 'NEW' ? <Add fontSize="small" /> :
                              change.changeType === 'RETURNING' ? <History fontSize="small" /> :
                              change.changeType === 'SECURITY_PIN_CHANGE' ? <Security fontSize="small" /> :
                              <Edit fontSize="small" />
                            }
                          />
                        </TableCell>
                        <TableCell>{change.field}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {change.oldValue || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="primary">
                            {change.newValue}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {validationResult.errors.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Errors Found:
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResult.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.rowNumber}</TableCell>
                        <TableCell>{error.studentName || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {error.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );

  const renderCompleteStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Import Complete
      </Typography>
      
      {importResult && (
        <>
          <Alert 
            severity={importResult.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
            icon={importResult.success ? <CheckCircle /> : <Error />}
          >
            {importResult.message}
          </Alert>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">School: {importResult.schoolName}</Typography>
            <Typography variant="body2">Total Rows: {importResult.totalRows}</Typography>
            <Typography variant="body2" color="success.main">Imported: {importResult.validRows}</Typography>
            {importResult.invalidRows > 0 && (
              <Typography variant="body2" color="error.main">Failed: {importResult.invalidRows}</Typography>
            )}
            
            {/* Enhanced Import Statistics */}
            {importResult.success && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'success.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Add fontSize="small" />
                  <Typography variant="body2" color="success.dark">
                    {importResult.newStudents || 0} New Students
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'secondary.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <History fontSize="small" />
                  <Typography variant="body2" color="secondary.dark">
                    {importResult.returningStudents || 0} Returning Students
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: 1,
                  p: 1, 
                  bgcolor: 'info.light', 
                  borderRadius: 1, 
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}>
                  <Edit fontSize="small" />
                  <Typography variant="body2" color="info.dark">
                    {importResult.updatedStudents || 0} Updated Students
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {importResult.errors.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Import Errors:
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Student</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResult.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>{error.rowNumber}</TableCell>
                        <TableCell>{error.studentName || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="error">
                            {error.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Students from XLSX</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {step === 'upload' && renderUploadStep()}
        {step === 'validate' && renderValidationStep()}
        {step === 'complete' && renderCompleteStep()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {step === 'complete' ? 'Close' : 'Cancel'}
        </Button>
        
        {step === 'upload' && selectedFile && (
          <Button 
            onClick={handleValidate} 
            variant="contained" 
            disabled={loading}
          >
            Validate
          </Button>
        )}
        
        {step === 'validate' && validationResult?.success && (
          <Button 
            onClick={handleImport} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            Import Students
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
